import { useQuery } from '@tanstack/react-query';
import { operations } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';

export type CompanyDocumentMetadataResponse =
  operations['getCompanyDocument']['responses'][200]['content']['application/json'];

export type CompanyDocumentMetadata = CompanyDocumentMetadataResponse['data'];

/**
 * Fetches a company document's real filename/size, for a document uploaded in a prior session
 * (where only its uuid is known, via company status — see `CompanyDocumentUpload`). Not called at
 * all for a file the user just picked in the current session, since its name/size are already
 * known client-side without a round trip.
 */
const useCompanyDocumentMetadata = (documentUuid: string | null) => {
  return useQuery({
    queryKey: ['companyDocumentMetadata', documentUuid],
    queryFn: () =>
      apiClient
        .get(`api/supplier-experience/companies/documents/${documentUuid}`)
        .json<CompanyDocumentMetadataResponse>()
        .then((response) => response.data),
    enabled: documentUuid !== null,
    // Overrides the app-wide throwOnError default — callers show an inline "couldn't load" message
    // via `isError` instead, so one document's fetch failure doesn't take down its whole card.
    throwOnError: false,
  });
};

export default useCompanyDocumentMetadata;
